import { Box, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from '@mui/icons-material/Edit';
import {
   getCustomerGroups,
   getProductGroups,
   getPGHistory,
   GetPGModificationDetails,
   setState,
   approvePG,
   rejectPG
} from "./cgpgSlice";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { setPGState as setProductGroupState, getPGDetail, getPGForCountry } from "../../Setup/Product/productGroupSetupSlice";
import muiConfig from '../../../assets/MuiConfig.json'
import { getPGHistoryRows, getLatestHistoryRowId, getClickedGridCellField, isCgpgGridActionField, getCgpgDetailPanelGridSx } from "./cgpgUtils";

const DetailPanelContext = createContext({ collapseDetailPanel: () => {} });

const CHECKBOX_COLUMN_WIDTH = 52;
const ACTION_COLUMN_WIDTH = 210;
const EDIT_COLUMN_WIDTH = 200;
const DETAIL_PANEL_TOGGLE_FIELD = "__detail_panel_toggle__";
const PRODUCT_GROUP_DETAIL_FIELDS = new Set([
   "productGroupName",
   "countryCode",
   "status",
   "updatedDate",
   "updatedBy",
   "reject",
   "approve",
   "edit",
]);

function getDetailPanelMetrics(columns) {
   const detailPanelWidth = columns
      .filter((column) => PRODUCT_GROUP_DETAIL_FIELDS.has(column.field))
      .reduce((sum, column) => sum + column.width, 0);

   return {
      detailPanelInset: CHECKBOX_COLUMN_WIDTH,
      detailPanelWidth,
   };
}

// #region getProductGroupsCol function
function getProductGroupsCol() {
   const width = 200;
   return [
      { field: "productGroupName", headerName: "Group Name", width: width },
      { field: "countryCode", headerName: "Country Code", width: width },
      { field: "status", headerName: "Status", width: width },
      { field: "updatedDate", headerName: "Last Action Date", width: width, valueFormatter: (params) =>moment(params.value).format('DD/MM/YYYY') },
      { field: "updatedBy", headerName: "Last Action By", width: width },
      {
         field: "reject",
         headerName: "Reject Product Group",
         width: 210,
         align:"center",
         renderCell: RejectButton,
      },
      {
         field: "approve",
         headerName: "Approve Product Group",
         width: 210,
         align:"center",
         renderCell: ApproveButton,
      },
      {
         field: "edit",
         headerName: "Edit",
         width: width,
         renderCell: EditButton,
         align:"center",
			headerAlign:"center",
      },
   ];
}
// #endregion

// #region Product Groups Table
export default function ProductGroupsTable() {
   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const { productGroupsTable, selectedRows, productGroups, pgHistory, selectedProductGroupHistoryID, isLoading, detailPanelCollapseToken } = states;
   const { rows } = productGroupsTable;
   const columns = getProductGroupsCol();
   const { detailPanelInset, detailPanelWidth } = getDetailPanelMetrics(columns);
   const [isAll, setIsAll] = useState(false);
   const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
   const [paginationModel, setPaginationModel] = useState({
      pageSize: 20,
      page: 0,
   });

   const loadProductGroupDetails = (productGroupID, packGroupModificationID) => {
      dispatch(getPGHistory({ productGroupID }))
         .unwrap()
         .then(() => {
            dispatch(
               GetPGModificationDetails({
                  packGroupID: productGroupID,
                  packGroupModificationID,
               })
            );
         })
         .catch(() => {});
   };

   const updateDetailPanel = (expandedRowIds) => {
      setDetailPanelExpandedRowIds(expandedRowIds);

      if (expandedRowIds.length === 0) {
         dispatch(setState("selectedProductGroupHistoryID", null));
         dispatch(setState("pgHistory", null));
         dispatch(setState("pG_Details", null));
         dispatch(setState("pgModificationDetails", null));
         return;
      }

      const productGroupID = expandedRowIds[expandedRowIds.length - 1];
      const clickedProductGroup = productGroups.find((pg) => pg.productGroupID === productGroupID);

      dispatch(setState("selectedProductGroupHistoryID", productGroupID));
      dispatch(setState("clickedRowComment", clickedProductGroup?.comments));
      dispatch(setState("groupRowClicked", true));
      loadProductGroupDetails(productGroupID, 0);
   };

   const collapseDetailPanel = () => {
      updateDetailPanel([]);
   };

   useEffect(() => {
      if (detailPanelCollapseToken === 0) {
         return;
      }
      collapseDetailPanel();
   }, [detailPanelCollapseToken]);

   const handleRowClick = (rowData, event) => {
      if (rowData == undefined) {
         return;
      }

      const clickedField = getClickedGridCellField(event);
      if (isCgpgGridActionField(clickedField)) {
         collapseDetailPanel();
         return;
      }

      const clickedProductGroupID = rowData?.row?.id;
      const clickedProductGroup = productGroups.find((pg) => pg.productGroupID === clickedProductGroupID);
      const isExpanded = detailPanelExpandedRowIds.includes(clickedProductGroupID);

      dispatch(setState("clickedRowComment", clickedProductGroup?.comments));
      dispatch(setState("groupRowClicked", true));
      updateDetailPanel(isExpanded ? [] : [clickedProductGroupID]);
   };

   const handleCellClick = (params, event) => {
      if (params.field === "reject" || params.field === "approve" || params.field === "edit") {
         event.stopPropagation();
      }
   };

   const getDetailPanelContent = ({ row }) => {
      if (row.id !== selectedProductGroupHistoryID) {
         return null;
      }

      if (isLoading && pgHistory === null) {
         return (
            <Box
               sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  width: detailPanelWidth,
               }}
            >
               <CircularProgress size={24} />
            </Box>
         );
      }

      return <PGHistoryDetail detailPanelWidth={detailPanelWidth} />;
   };

   const setModal = (newPaginationModel) => {
      if(newPaginationModel.pageSize === muiConfig.DataGrid_All && newPaginationModel.page === 0 && rows?.length > 100){
      setIsAll(true);
      }else{
      setIsAll(false);
      }
      setPaginationModel(newPaginationModel);
   }
   return (
      <DetailPanelContext.Provider value={{ collapseDetailPanel }}>
      <Box sx={{ mt: 3 }}>
         <DataGridPro
            sx={{
               "& .MuiDataGrid-columnHeaders":{
                  backgroundColor:"grey.lighter"
               },
               [`& .MuiDataGrid-columnHeader[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"], & .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
                  display: "none",
                  width: "0 !important",
                  minWidth: "0 !important",
                  maxWidth: "0 !important",
                  padding: "0 !important",
                  border: "none",
               },
               ...getCgpgDetailPanelGridSx(detailPanelInset, detailPanelWidth),
               ...(!isAll ? {
                  "& .MuiDataGrid-virtualScroller": {
                     maxHeight: "unset !important"
                  }
                } : {
                  "& .MuiDataGrid-virtualScroller": {
                     maxHeight: "80vh !important"
                  }
               })
            }}
            slots={{
               detailPanelCollapseIcon: () => null,
               detailPanelExpandIcon: () => null,
            }}
            rows={rows}
            columns={columns}
            onRowSelectionModelChange={(selRows) => {
               dispatch(setState("selectedRows", selRows));
            }}
            rowSelectionModel={selectedRows}
            isRowSelectable={(params) => params.row.canApproveReject === true}
            checkboxSelection
            disableColumnMenu
            disableRowSelectionOnClick
            initialState={{
					pagination: { paginationModel: { pageSize:20, page:0 }}
				}}
				pagination
				pageSizeOptions={[20, 50, 100, { value: muiConfig.DataGrid_All , label: 'All' }]}
            paginationModel={paginationModel}
            onPaginationModelChange = {setModal}
            getRowClassName={(params) =>
					params.row.canApproveReject !== true ? 'disabled-row' : ''
				}
            onRowClick={handleRowClick}
            onCellClick={handleCellClick}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={updateDetailPanel}
            getDetailPanelHeight={() => "auto"}
            getDetailPanelContent={getDetailPanelContent}
         />
      </Box>
      </DetailPanelContext.Provider>
   );
}
// #endregion

// #region Reject Button
function RejectButton(params) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      productGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: "",
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }
   function openRejectModal() {
      collapseDetailPanel();
      let id = params.row.id;
      let productGroup = productGroups.find((pg) => pg.productGroupID === id);

      dispatch(setState("selectedProductGroupName", productGroup.productGroupName));
      dispatch(setState("selectedProductGroupID",id));
      dispatch(setState("rejectSingleModalOpen", true));
	}



   if (params.row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={openRejectModal}>
            <CancelIcon sx={{ color: "red.main" }} />
         </IconButton>
      );
   }
   return null;
}
// #endregion

// #region Approve Button
function ApproveButton(params) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      productGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView,
      searchText
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: searchText,
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }

   function approve() {
      collapseDetailPanel();
      const id = params.row.id;
      const productGroup = productGroups.find((pg) => pg.productGroupID === id);
      const logedUser = JSON.parse(localStorage.getItem("data"));
      const obj = {
         ...productGroup,
         isApprove: 1,
         userRole:logedUser.roleName
      };
      console.log("Approval_CGPG_ApprovePG", obj);
      dispatch(approvePG(obj)).then(() => {
			search();
		});
   }

   if (params.row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={approve}>
            <CheckCircleIcon sx={{ color: "green.main" }} />
         </IconButton>
      );
   }
   return null;
}
//#endregion

// #region Edit Button
function EditButton(params) {
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const navigate = useNavigate();
	const dispatch = useDispatch();

   const productGroupApprovalState = useSelector((store) => store.cgpgApproval);
   const {productGroups,countries}=productGroupApprovalState;

   function gotoEditProductGroup() {
      const id = params.row.id;
      const productGroup = productGroups.find((pg) => pg.productGroupID === id);
      if (!productGroup) {
         return;
      }

      const countryRow = countries.find(
         (country) => String(country.countryID) === String(productGroup.countryID)
      );
      if (!countryRow) {
         return;
      }

      const { productGroupName, productGroupID, countryID } = productGroup;
      dispatch(setProductGroupState("isEdit", true));
      dispatch(setProductGroupState("country", countryRow.countryName));
      dispatch(setProductGroupState("countryID", countryRow.countryID));
      dispatch(setProductGroupState("productGroupNameSearch", productGroupName ?? ""));
      dispatch(setProductGroupState("productGroupName", productGroupName ?? ""));
      dispatch(setProductGroupState("productGroupID", productGroupID));
      dispatch(setProductGroupState("isNavigate", true));
      dispatch(
         getPGForCountry({
            countryID,
            productGroupNameSearch: productGroupName ?? "",
         })
      );
      dispatch(getPGDetail({ productGroupID }));
      navigate("/setup/product-group");
   }
   if (params.row.canEdit && isRestrictedAccess) {
   return (
            <IconButton sx={{
               padding:0,
               '&:hover':{
                        backgroundColor:'#F8E9E7',
                        borderRadius:'2px',
                     }
            }}>
            <EditIcon onClick={gotoEditProductGroup} 
            sx={{ 
               color: "#000000",
               fontSize:'large', 
               justifyContent: 'center',
               borderRadius:'2px',
               width:25,
               height:22,
               backgroundColor: 'transparent',
               }} />
         </IconButton>
   );
}
}
//#endregion

function PGHistoryDetail({ detailPanelWidth }) {
   const dispatch = useDispatch();
   const { pgHistory, selectedProductGroupHistoryID } = useSelector((store) => store.cgpgApproval);
   const historyRows = getPGHistoryRows(pgHistory);
   const latestHistoryRowId = getLatestHistoryRowId(historyRows);

   const handleHistoryRowClick = (item) => {
      const packGroupID = item.packGroupID ?? selectedProductGroupHistoryID;
      const packGroupModificationID = item.packGroupModificationID;

      if (!packGroupID || packGroupModificationID == null) {
         return;
      }

      dispatch(setState("clickedRowComment", item.comments ?? ""));
      dispatch(setState("groupRowClicked", true));
      dispatch(
         GetPGModificationDetails({
            packGroupID,
            packGroupModificationID,
         })
      );
   };

   if (historyRows.length === 0) {
      return (
         <Box sx={{ py: 2, px: 2, width: detailPanelWidth }}>
            No history found.
         </Box>
      );
   }

   return (
      <TableContainer
         component={Paper}
         elevation={0}
         sx={{ width: detailPanelWidth, overflowX: "hidden" }}
      >
         <Table size="small" sx={{ width: "100%" }}>
            <TableHead
               sx={{
                  "& th": {
                     backgroundColor: "#EEF2F4!important",
                     fontSize: "12px",
                  },
               }}
            >
               <TableRow>
                  <TableCell>Submission No</TableCell>
                  <TableCell>Submission Status</TableCell>
                  <TableCell>Last Action Date</TableCell>
                  <TableCell>Last Action By</TableCell>
                  <TableCell align="center" sx={{ width: ACTION_COLUMN_WIDTH }}>
                     Reject Product Group
                  </TableCell>
                  <TableCell align="center" sx={{ width: ACTION_COLUMN_WIDTH }}>
                     Approve Product Group
                  </TableCell>
                  <TableCell align="center" sx={{ width: EDIT_COLUMN_WIDTH }}>
                     Edit
                  </TableCell>
               </TableRow>
            </TableHead>
            <TableBody
               sx={{
                  "& tr": {
                     backgroundColor: "rgb(249, 251, 254)",
                     fontSize: "12px",
                  },
               }}
            >
               {historyRows.map((item) => (
                  <TableRow
                     key={item.id}
                     hover
                     sx={{ cursor: "pointer" }}
                     onClick={() => handleHistoryRowClick(item)}
                  >
                     <TableCell>
                        {item.submissionNumber}
                     </TableCell>
                     <TableCell>
                        {item.status}
                     </TableCell>
                     <TableCell>
                        {item.modifiedDate && moment(item.modifiedDate).format("DD/MM/YYYY")}
                     </TableCell>
                     <TableCell>
                        {item.modifiedBy}
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: ACTION_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <PGHistoryRejectButton
                           row={item}
                           productGroupID={selectedProductGroupHistoryID}
                        />
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: ACTION_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <PGHistoryApproveButton
                           row={item}
                           productGroupID={selectedProductGroupHistoryID}
                        />
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: EDIT_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <PGHistoryEditButton
                           row={item}
                           productGroupID={selectedProductGroupHistoryID}
                           isLatestSubmission={item.id === latestHistoryRowId}
                        />
                     </TableCell>
                  </TableRow>
               ))}
            </TableBody>
         </Table>
      </TableContainer>
   );
}

function PGHistoryRejectButton({ row, productGroupID }) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const { productGroups } = states;

   function openRejectModal() {
      collapseDetailPanel();
      const productGroup = productGroups.find((pg) => pg.productGroupID === productGroupID);

      dispatch(setState("selectedProductGroupName", productGroup?.productGroupName ?? ""));
      dispatch(setState("selectedProductGroupID", productGroupID));
      dispatch(setState("rejectSingleModalOpen", true));
   }

   if (row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={openRejectModal}>
            <CancelIcon sx={{ color: "red.main" }} />
         </IconButton>
      );
   }

   return null;
}

function PGHistoryApproveButton({ row, productGroupID }) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      productGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView,
      searchText,
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: searchText,
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }

   function approve() {
      collapseDetailPanel();
      const productGroup = productGroups.find((pg) => pg.productGroupID === productGroupID);
      const logedUser = JSON.parse(localStorage.getItem("data"));
      const obj = {
         ...productGroup,
         isApprove: 1,
         userRole: logedUser.roleName,
      };

      dispatch(approvePG(obj)).then(() => {
         search();
      });
   }

   if (row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={approve}>
            <CheckCircleIcon sx={{ color: "green.main" }} />
         </IconButton>
      );
   }

   return null;
}

function PGHistoryEditButton({ row, productGroupID, isLatestSubmission }) {
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { productGroups, countries } = useSelector((store) => store.cgpgApproval);

   function gotoEditProductGroup() {
      const productGroup = productGroups.find((pg) => pg.productGroupID === productGroupID);

      if (!productGroup) {
         return;
      }

      const countryRow = countries.find(
         (country) => String(country.countryID) === String(productGroup.countryID)
      );
      if (!countryRow) {
         return;
      }

      const { productGroupID: selectedProductGroupID, productGroupName, countryID } =
         productGroup;
      dispatch(setProductGroupState("isEdit", true));
      dispatch(setProductGroupState("country", countryRow.countryName));
      dispatch(setProductGroupState("countryID", countryRow.countryID));
      dispatch(setProductGroupState("productGroupNameSearch", productGroupName ?? ""));
      dispatch(setProductGroupState("productGroupName", productGroupName ?? ""));
      dispatch(setProductGroupState("productGroupID", selectedProductGroupID));
      dispatch(setProductGroupState("isNavigate", true));
      dispatch(
         getPGForCountry({
            countryID,
            productGroupNameSearch: productGroupName ?? "",
         })
      );
      dispatch(getPGDetail({ productGroupID: selectedProductGroupID }));
      navigate("/setup/product-group");
   }

   if (row.canEdit && isRestrictedAccess && isLatestSubmission) {
      return (
         <IconButton
            sx={{
               padding: 0,
               "&:hover": {
                  backgroundColor: "#F8E9E7",
                  borderRadius: "2px",
               },
            }}
         >
            <EditIcon
               onClick={gotoEditProductGroup}
               sx={{
                  color: "#000000",
                  fontSize: "large",
                  justifyContent: "center",
                  borderRadius: "2px",
                  width: 25,
                  height: 22,
                  backgroundColor: "transparent",
               }}
            />
         </IconButton>
      );
   }

   return null;
}